import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CalendarClock, Flame, Landmark, Receipt, RefreshCw, SlidersHorizontal, Sun } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, EmptyState, KpiCard, PillTabs, formatCurrency, formatDate, formatDateTime, formatNumber } from '../../../components/design-system/admin';
import { usePlatformCostsApi } from '../../../common/hooks/use-platform-costs-api.hook';
import { PROVIDER_NAMES, type CostDays, type CostProviderId, type CostsResponse } from '../../../common/models/platform-costs.model';
import { useAdminNav } from '../../../modules/superadmin/shell/admin-nav.store';
import { AttentionBanner } from '../../../modules/superadmin/costs/AttentionBanner';
import { BreakdownCard } from '../../../modules/superadmin/costs/BreakdownCard';
import { CostEventsCard } from '../../../modules/superadmin/costs/CostEventsCard';
import { CostSettingsDialog } from '../../../modules/superadmin/costs/CostSettingsDialog';
import { DevEnvironmentCard } from '../../../modules/superadmin/costs/DevEnvironmentCard';
import { ProviderCard } from '../../../modules/superadmin/costs/ProviderCard';
import { RunningNowCard } from '../../../modules/superadmin/costs/RunningNowCard';
import { ShareCard } from '../../../modules/superadmin/costs/ShareCard';
import { SpendChart } from '../../../modules/superadmin/costs/SpendChart';
import { money, moneyShort } from '../../../modules/superadmin/costs/cost-format';

dayjs.extend(relativeTime);

const RANGE_KEY = 'ardealy.admin.costs.days';
/** The page re-reads the (snapshot-cached) numbers this often while open. */
const POLL_MS = 60_000;

function initialDays(): CostDays {
  try {
    const v = Number(localStorage.getItem(RANGE_KEY));
    return v === 7 || v === 90 ? v : 30;
  } catch {
    return 30;
  }
}

/** Money spent since `since` at `perHour`, ticking every second: the burn made visible. */
function useMeter(perHour: number, since: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (perHour * (now - since)) / 3_600_000;
}

/**
 * What Ardealy pays to run, across every provider: live balances with how
 * long they last, spend today / this month / forecast, where it goes, what is
 * running by the hour, and the way to top each one up.
 */
export const PlatformCostsPage = () => {
  const { t } = useTranslation();
  const api = usePlatformCostsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [days, setDays] = useState<CostDays>(initialDays);
  const [data, setData] = useState<CostsResponse | null>(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openedAt] = useState(Date.now());

  const load = useCallback(async (range: CostDays, fresh = false) => {
    setError(false);
    try {
      const r = await apiRef.current.getCosts(range, fresh);
      if (r?.totals) {
        setData(r);
        useAdminNav.setState({ costsAttention: r.totals.attention });
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    setData((d) => (d && d.days === days ? d : null));
    load(days);
    const id = setInterval(() => document.visibilityState === 'visible' && load(days), POLL_MS);
    return () => clearInterval(id);
  }, [days, load]);

  const changeDays = (d: CostDays) => {
    setDays(d);
    try {
      localStorage.setItem(RANGE_KEY, String(d));
    } catch {
      // storage blocked: the range just isn't remembered
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await load(days, true);
    setRefreshing(false);
  };

  const tt = data?.totals;
  const meter = useMeter(tt?.burnPerHourUsd || 0, openedAt);
  const thresholds = Object.fromEntries((data?.providers || []).map((p) => [p.provider, p.thresholdUsd])) as Partial<Record<CostProviderId, number | null>>;
  const forecastHint = tt
    ? tt.budgetUsd
      ? t('ADMIN_COSTS.KPI.FORECAST_BUDGET', { amount: moneyShort(tt.budgetUsd), percent: formatNumber((tt.forecastMonthUsd / tt.budgetUsd) * 100) })
      : t('ADMIN_COSTS.KPI.FORECAST_LAST', { amount: moneyShort(tt.prevMonthUsd) })
    : null;
  const lowest = tt?.lowestRunway;

  return (
    <AdminPage
      title={t('ADMIN_COSTS.TITLE')}
      header={
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('ADMIN_COSTS.TITLE')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('ADMIN_COSTS.SUBTITLE')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" title={formatDateTime(data.generatedAt)}>
                <span className="relative flex size-1.5" aria-hidden>
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60 motion-reduce:animate-none" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-success" />
                </span>
                {t('ADMIN_COSTS.UPDATED', { time: dayjs(data.generatedAt).fromNow() })}
              </span>
            )}
            <Button variant="ghost" size="icon-sm" onClick={refresh} disabled={refreshing} aria-label={t('ADMIN_COSTS.REFRESH')} title={t('ADMIN_COSTS.REFRESH')}>
              <RefreshCw className={refreshing ? 'animate-spin' : undefined} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} disabled={!data} data-testid="costs-open-settings">
              <SlidersHorizontal />
              {t('ADMIN_COSTS.SETTINGS')}
            </Button>
            <PillTabs<CostDays>
              value={days}
              onChange={changeDays}
              ariaLabel={t('ADMIN_COSTS.RANGE_LABEL')}
              options={[
                { value: 7, label: t('ADMIN_COSTS.RANGE_7') },
                { value: 30, label: t('ADMIN_COSTS.RANGE_30') },
                { value: 90, label: t('ADMIN_COSTS.RANGE_90') },
              ]}
            />
          </div>
        </div>
      }
    >
      {error && !data ? (
        <EmptyState
          title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
          action={
            <Button variant="outline" onClick={() => load(days, true)}>
              {t('ADMIN_SHELL.COMMON.RETRY')}
            </Button>
          }
          className="py-24"
        />
      ) : (
        <div className="flex flex-col gap-4">
          {data && <AttentionBanner providers={data.providers} />}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="costs-kpis">
            <KpiCard
              label={t('ADMIN_COSTS.KPI.TODAY')}
              icon={<Sun />}
              loading={!data}
              value={money(tt?.todayUsd)}
              hint={tt ? t('ADMIN_COSTS.KPI.TODAY_HINT', { amount: money(tt.yesterdayUsd) }) : null}
              testId="costs-kpi-today"
            />
            <KpiCard
              label={t('ADMIN_COSTS.KPI.MONTH')}
              icon={<Receipt />}
              loading={!data}
              value={moneyShort(tt?.monthUsd)}
              delta={tt ? { current: tt.monthUsd, previous: tt.prevMonthToDateUsd } : undefined}
              invert
              hint={t('ADMIN_COSTS.KPI.MONTH_HINT')}
              trend={data?.series.map((p) => p.total)}
              testId="costs-kpi-month"
            />
            <KpiCard
              label={t('ADMIN_COSTS.KPI.FORECAST')}
              icon={<CalendarClock />}
              loading={!data}
              value={moneyShort(tt?.forecastMonthUsd)}
              hint={forecastHint}
              testId="costs-kpi-forecast"
            />
            <KpiCard
              label={t('ADMIN_COSTS.KPI.BURN')}
              icon={<Flame />}
              loading={!data}
              value={tt ? t('ADMIN_COSTS.KPI.BURN_VALUE', { amount: money(tt.burnPerHourUsd) }) : ''}
              hint={
                tt ? (
                  <span className="tabular-nums">
                    {t('ADMIN_COSTS.KPI.BURN_HINT', { day: moneyShort(tt.burnPerHourUsd * 24), meter: formatCurrency(meter, 'usd', 4) })}
                  </span>
                ) : null
              }
              testId="costs-kpi-burn"
            />
            <KpiCard
              label={t('ADMIN_COSTS.KPI.BALANCE')}
              icon={<Landmark />}
              loading={!data}
              value={moneyShort(tt?.balanceUsd)}
              hint={
                lowest
                  ? t('ADMIN_COSTS.KPI.BALANCE_HINT', { provider: PROVIDER_NAMES[lowest.provider], days: lowest.days < 10 ? lowest.days.toFixed(1) : Math.round(lowest.days) })
                  : t('ADMIN_COSTS.KPI.BALANCE_NONE')
              }
              testId="costs-kpi-balance"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="costs-providers">
            {data
              ? data.providers.map((p) => <ProviderCard key={p.provider} p={p} />)
              : Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
          </div>

          <DevEnvironmentCard />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {data ? (
              <>
                <SpendChart series={data.series} providers={data.providers} className="lg:col-span-2" />
                <ShareCard providers={data.providers} days={data.days} />
              </>
            ) : (
              <>
                <Skeleton className="h-80 rounded-xl lg:col-span-2" />
                <Skeleton className="h-80 rounded-xl" />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {data ? (
              <>
                <BreakdownCard data={data} className="lg:col-span-2" />
                <div className="flex flex-col gap-4">
                  <RunningNowCard providers={data.providers} />
                  <CostEventsCard events={data.events} />
                </div>
              </>
            ) : (
              <Skeleton className="h-96 rounded-xl lg:col-span-3" />
            )}
          </div>

          {data && (
            <div className="flex flex-col gap-1 text-[11px] text-muted-foreground">
              <p>
                {t('ADMIN_COSTS.FOOTNOTE.USD', {
                  rate: formatNumber(data.fx.eurUsd, 4),
                  source: `${data.fx.source === 'ecb' ? t('ADMIN_COSTS.FOOTNOTE.ECB') : t('ADMIN_COSTS.FOOTNOTE.FALLBACK')}${data.fx.source === 'ecb' ? `, ${formatDate(data.fx.at)}` : ''}`,
                })}
              </p>
              <p>{t('ADMIN_COSTS.FOOTNOTE.TOPUP')}</p>
            </div>
          )}
        </div>
      )}

      {data && (
        <CostSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={data.settings} thresholds={thresholds} onSaved={() => load(days, true)} />
      )}
    </AdminPage>
  );
};

export default PlatformCostsPage;
